import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, Loader2, Search } from 'lucide-react';
import { identiteParSiret } from '../services/api';

type Ligne = {
  cle: string;
  label: string;
  actuelle: string | null;
  proposee: string;
  identique: boolean;
  applique: boolean;
};

/**
 * Remplir l'identité de l'association à partir de son SIRET.
 *
 * Le répertoire des entreprises sait tout ce que les mentions légales
 * réclament : raison sociale, adresse, code NAF, forme juridique, numéro de
 * TVA. Le recopier à la main depuis un avis de situation, c'est dix champs à
 * taper sans se tromper — et un code NAF de travers ne se voit qu'à la
 * première facture contestée.
 *
 * En deux temps, jamais en un seul : on regarde ce que le répertoire propose,
 * puis on reporte. Une association dont l'adresse de gestion diffère de son
 * siège déclaré doit pouvoir garder la sienne, et c'est le cas le plus
 * fréquent — le siège est souvent la mairie.
 *
 * Beaucoup d'associations n'ont pas de SIRET du tout : le bloc reste alors
 * sans effet, et les champs se saisissent à la main comme avant.
 */
export default function IdentiteParSiret() {
  const queryClient = useQueryClient();
  const [numero, setNumero] = useState('');
  const [ecraser, setEcraser] = useState(false);
  const [resultat, setResultat] = useState<{
    trouve: boolean;
    active?: boolean;
    lignes?: Ligne[];
    message: string;
    appliquee?: boolean;
  } | null>(null);

  const interroger = useMutation({
    mutationFn: (appliquer: boolean) => identiteParSiret(numero, appliquer, ecraser),
    onSuccess: (d: any) => {
      setResultat(d);
      if (d.appliquee) {
        queryClient.invalidateQueries({ queryKey: ['admin-parametres'] });
      }
    },
    onError: (e: Error) => setResultat({ trouve: false, message: e.message }),
  });

  const lignes = resultat?.lignes ?? [];
  const aReporter = lignes.filter((l) => !l.identique && (ecraser || l.actuelle === null));

  return (
    <div className="mb-5 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        <Building2 className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Remplir depuis le répertoire des entreprises
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            Saisissez votre SIRET (14 chiffres) ou votre SIREN (9) : raison sociale, adresse, code
            NAF, forme juridique et numéro de TVA se renseignent d'un coup. Rien n'est modifié tant
            que vous ne l'avez pas demandé.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          className="input flex-1 min-w-[12rem] tabular-nums"
          inputMode="numeric"
          placeholder="123 456 789 00012"
          value={numero}
          onChange={(e) => { setNumero(e.target.value); setResultat(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && numero.trim()) interroger.mutate(false); }}
        />
        <button
          type="button"
          onClick={() => interroger.mutate(false)}
          disabled={interroger.isPending || numero.replace(/\D/g, '').length < 9}
          className="btn-outline inline-flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
        >
          {interroger.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Rechercher
        </button>
      </div>

      {resultat && !resultat.trouve && (
        <p className="text-sm rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-200">
          {resultat.message}
        </p>
      )}

      {resultat?.trouve && resultat.active === false && (
        <p className="text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-red-800">
          Attention : cet établissement est déclaré cessé au répertoire. Vérifiez le numéro avant
          d'émettre quoi que ce soit à ce nom.
        </p>
      )}

      {resultat?.trouve && lignes.length > 0 && (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="font-medium pb-1 pr-3">Champ</th>
                  <th className="font-medium pb-1 pr-3">Actuel</th>
                  <th className="font-medium pb-1">Au répertoire</th>
                </tr>
              </thead>
              <tbody className="[&_td]:py-1 [&_tr]:border-t [&_tr]:border-gray-100 dark:[&_tr]:border-gray-800">
                {lignes.map((l) => (
                  <tr key={l.cle} className={l.identique ? 'text-gray-400' : ''}>
                    <td className="pr-3 whitespace-nowrap">{l.label}</td>
                    <td className="pr-3 text-gray-500 dark:text-gray-400">{l.actuelle ?? '—'}</td>
                    <td className={l.identique ? '' : 'font-medium text-gray-900 dark:text-gray-100'}>
                      {l.proposee}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Le siège déclaré est souvent la mairie, et l'adresse de gestion
              celle du président : écraser d'office serait une correction que
              personne n'a demandée. */}
          <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={ecraser}
              onChange={(e) => setEcraser(e.target.checked)}
              className="mt-0.5 accent-primary-600"
            />
            <span>
              Remplacer aussi les champs déjà renseignés. Sans cette case, seuls les champs vides
              sont complétés — votre adresse de gestion reste la vôtre.
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => interroger.mutate(true)}
              disabled={interroger.isPending || aReporter.length === 0}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {interroger.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Reporter {aReporter.length > 0 ? `${aReporter.length} champ${aReporter.length > 1 ? 's' : ''}` : ''}
            </button>
            {aReporter.length === 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Rien à reporter : tout concorde déjà.
              </span>
            )}
          </div>
        </div>
      )}

      {resultat?.appliquee && (
        <p className="text-sm rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2 text-green-800 dark:text-green-200">
          {resultat.message}
        </p>
      )}
    </div>
  );
}
